import { useRaysyncStore } from '@/store/raysync/raysync-store'
import { useEffect } from 'react'
import { getTranslateNsT } from '@/utils/get-translate-nst'
import RaySyncIns from '@/services/raysync'
import { ConnectStatus } from 'project-config/constants'
import {
  CHECK_SUCCESS,
  CONNECT_FAILED,
  HAS_UPGRADE_VERSION,
  LOGGED_OUT,
  PING_FAILED,
  PING_SUCCESS,
} from 'project-config/services/raysync/event-type'
import { useTransferLine } from './use-transfer-line'
import { useModalStore } from '@/store/modal/modal-store'
import { RaysyncUpdateModal } from '@/components/modal/raysync-update-modal'
import { ConnectError, ServerError } from 'project-config/services/raysync/model-type'
import { useOutputStore } from '@/store/output/output-store'
import { getShareKey } from '@/utils'

export function useRaysync() {
  const {
    raysyncSoftInfo,
    raysyncClientVersion,
    fetchRaysyncSoftInfo,
    setShowRaysyncModal,
    resetRaysyncModalTip,
    setRaysyncNetError,
    setRaysyncClientVersion,
    setRaysyncModalTip,
  } = useRaysyncStore()
  const { setState } = useRaysyncStore
  const raysyncT = getTranslateNsT('raysync')
  const commonT = getTranslateNsT('common')
  const { getCurrentTransferLine } = useTransferLine()
  const { showGlobalModal, hideGlobalModal } = useModalStore()
  const fileShareInfo = useOutputStore((state) => state.fileShareInfo)

  useEffect(() => {
    setState({
      raysyncModalTitle: commonT('common_dialog_title'),
    })
  }, [])

  useEffect(() => {
    if (!raysyncSoftInfo) {
      fetchRaysyncSoftInfo()
    }
  }, [raysyncSoftInfo])

  const handleConnectFailed = (code: any) => {
    const raysyncConnectStatus = useRaysyncStore.getState().raysyncConnectStatus
    const serverErrors = [ServerError.RequestBeforeLogin]
    const connectErrors = [
      ConnectError.ProxyTimeout,
      ConnectError.Disconnect,
      ConnectError.ExecuteTimeout,
      ConnectError.Closed,
    ]
    if (connectErrors.includes(code)) {
      setRaysyncModalTip(raysyncT('connect_error'))
      setShowRaysyncModal(true)
      setRaysyncNetError(true)
    } else if (serverErrors.includes(code)) {
      setRaysyncModalTip(raysyncT('server_error'))
      setShowRaysyncModal(true)
      setRaysyncNetError(true)
    }
    if (
      raysyncConnectStatus === ConnectStatus.FAILED ||
      raysyncConnectStatus === ConnectStatus.CLOSED
    ) {
      resetRaysyncModalTip()
      setShowRaysyncModal(true)
    }
  }

  const handlePingSuccess = () => {
    setRaysyncNetError(false)
    setShowRaysyncModal(false)
  }

  const handleUpgrade = () => {
    showGlobalModal({
      type: 'blank',
      render: <RaysyncUpdateModal onClose={hideGlobalModal} />,
    })
  }

  const handleLoggedOut = () => {
    setRaysyncNetError(false)
  }

  const handleCheckSuccess = (data: any) => {
    setRaysyncNetError(false)
    setShowRaysyncModal(false)
    setRaysyncClientVersion(data.clientVersion)
  }

  useEffect(() => {
    const init = () => {
      if (fileShareInfo?.raySyncKey) {
        Object.assign(window, { RaySyncIns })
        const raySyncUserKey = fileShareInfo?.raySyncKey
        const shareKey = getShareKey()

        if (!raySyncUserKey) {
          console.error('has login status without raysync userKey')
          return
        }
        RaySyncIns.initUserInfo(
          shareKey,
          raySyncUserKey,
          '',
          fileShareInfo?.platform?.toString() ?? '',
        )

        resetRaysyncModalTip()
        RaySyncIns.subscribe(PING_SUCCESS, handlePingSuccess)
        RaySyncIns.subscribe(CHECK_SUCCESS, handleCheckSuccess)
        RaySyncIns.subscribe(HAS_UPGRADE_VERSION, handleUpgrade)
        RaySyncIns.subscribe(LOGGED_OUT, handleLoggedOut)
        RaySyncIns.handleError = handleConnectFailed
        Object.assign(window, { RaySyncIns })
        RaySyncIns.ping()
          .then(() => {
            RaySyncIns.subscribe(CONNECT_FAILED, handleConnectFailed)
          })
          .catch((e) => console.error(e))
      }
    }
    void init()
    return () => {
      RaySyncIns.unsubscribe(HAS_UPGRADE_VERSION)
      RaySyncIns.unsubscribe(PING_FAILED)
      RaySyncIns.unsubscribe(PING_SUCCESS)
      RaySyncIns.unsubscribe(LOGGED_OUT)
    }
  }, [fileShareInfo?.raySyncKey])

  useEffect(() => {
    const init = async () => {
      if (raysyncClientVersion) {
        const line = await getCurrentTransferLine()
        const raySyncUserKey = fileShareInfo?.raySyncKey
        const shareKey = getShareKey()
        RaySyncIns.initUserInfo(
          shareKey,
          raySyncUserKey ?? '',
          line,
          fileShareInfo?.platform?.toString() ?? '',
        )
      }
    }
    void init()
  }, [fileShareInfo?.raySyncKey, raysyncClientVersion])
}
